/**
 * useSendMessage Hook - PPT Add-in specific implementation
 * Sets up the message service for the shared useSendMessage hook
 */

import { useEffect } from "react";
import { 
  sendTaskpaneChatMessage, 
  initializeTaskpaneChatService, 
  cancelTaskpaneChatInterview,
  cancelTaskpaneChatTask,
  createTaskpaneChatConversation 
} from "../services/taskpaneChatService";
import {
  createLiveChatMessageService,
} from "@carousel-office/shared";
import {
  useSendMessage as useSharedSendMessage,
  MessageService,
  OperationCallbacks as SharedOperationCallbacks,
} from "@carousel-office/shared/hooks/useSendMessage";
import { AgentCallbacks } from "../../main/core/types";
import { addDebugResult } from "../components/DebugPanel";

const taskpaneMessageService = createLiveChatMessageService({
  serviceName: "TaskpaneMessageService",
  ensureBootstrapped: initializeTaskpaneChatService,
  buildService: async () => {
    const service: MessageService = {
      sendMessage: async (
        message: string,
        conversationId: string,
        onChunk?: (chunk: string) => void,
        operationCallbacks?: SharedOperationCallbacks,
        options?: {
          context?: Array<{
            context: string[];
            purpose: string;
          }>;
        }
      ) => {
        // Cancel any pending interview before sending new message
        // This handles the case where user ignores the interview and just types something
        cancelTaskpaneChatInterview();
        
        // Build AgentCallbacks from shared callbacks and onChunk
        const callbacks: AgentCallbacks | undefined = (onChunk || operationCallbacks) ? {
          onChunk,
          onToolStart: operationCallbacks?.onToolStart,
          onOperationField: operationCallbacks?.onOperationField,
          onToolResult: (toolName, result) => {
            // Feed to debug panel
            addDebugResult('orchestrator', toolName, result);
            // Call original callback
            operationCallbacks?.onToolResult?.(toolName, result);
          },
          onAgentChunk: operationCallbacks?.onAgentChunk,
          onAgentToolStart: operationCallbacks?.onAgentToolStart,
          onAgentOperationField: operationCallbacks?.onAgentOperationField,
          onAgentToolCall: operationCallbacks?.onAgentToolCall,
          onAgentToolResult: (agentName, toolName, result) => {
            // Feed to debug panel - captures ImageSearchAgent results
            addDebugResult(agentName, toolName, result);
            // Call original callback
            operationCallbacks?.onAgentToolResult?.(agentName, toolName, result);
          },
        } : undefined;
        
        // Send message with conversationId for proper routing through AgentRegistry
        const response = await sendTaskpaneChatMessage(message, conversationId, callbacks, options?.context);
        return { content: response?.content ?? '' };
      },
      initialize: async () => {
        await initializeTaskpaneChatService();
      },
      /**
       * Called when a new conversation is created
       * Creates the conversation with an orchestrator in the GlobalStateManager
       */
      onNewConversation: async (conversationId: string) => {
        console.log('[useSendMessage] Creating new conversation with orchestrator:', conversationId);
        await createTaskpaneChatConversation(conversationId);
      },
      /**
       * Cancel the current running task for a conversation
       * This sets the cancellation token so the agent will stop at the next check point
       */
      cancel: (conversationId: string) => {
        console.log('[useSendMessage] 🛑 Cancelling task for conversation:', conversationId);
        cancelTaskpaneChatTask(conversationId);
      }
    };
    return service;
  },
});

/**
 * Initialize the message service for ppt-addin
 * This should be called during app initialization
 */
export async function initializeMessageService() {
  try {
    await taskpaneMessageService.initialize();
    console.log('[useSendMessage] Taskpane message service initialized');
  } catch (err) {
    console.error('[useSendMessage] Failed to initialize taskpane chat service:', err);
    throw err;
  }
}

/**
 * Hook that initializes the service and returns the shared useSendMessage hook
 */
export const useSendMessage = () => {
  // Initialize service on mount
  useEffect(() => {
    initializeMessageService().catch(err => {
      console.error('[useSendMessage] Failed to initialize message service:', err);
    });
  }, []);

  // Return the shared implementation
  return useSharedSendMessage();
};
